import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Plus, Search, HardHat, Loader2, Archive, RotateCcw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { projectsApi, projectFinancialsApi } from '../api';
import { ProjectFormDialog } from '../components/ProjectFormDialog';
import { RecentlyDeletedDialog } from '../components/RecentlyDeletedDialog';
import { ProjectStatusBadge } from '../components/ProjectStatusBadge';
import {
  STATUS_OPTIONS,
  TYPE_OPTIONS,
  SORT_OPTIONS,
  statusLabel,
  typeLabel,
  formatLocation,
  fmtDate,
  centsToDollarString,
  isOverdue,
} from '../constants';
import { budgetHealthLabel, BUDGET_HEALTH_STYLES } from '../financialConstants';

const selectCls =
  'bg-[#1A1A1A] border border-gray-700 text-white rounded-md px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none';

export const OpsProjects = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [finMap, setFinMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [trashOpen, setTrashOpen] = useState(false);

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [projectType, setProjectType] = useState('');
  const [archived, setArchived] = useState(false);
  const [sort, setSort] = useState('recently_updated');

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const params = { archived, sort };
      if (search.trim()) params.search = search.trim();
      if (status) params.status = status;
      if (projectType) params.project_type = projectType;
      const res = await projectsApi.list(params);
      setProjects(res.data);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [search, status, projectType, archived, sort]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setDialogOpen(true);
      searchParams.delete('new');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // One batch call for financial health badges (no per-row requests)
    projectFinancialsApi.list()
      .then((r) => {
        const map = {};
        r.data.forEach((f) => { map[f.project.id] = f; });
        setFinMap(map);
      })
      .catch(() => {});
  }, []);

  const handleSaved = (project) => {
    navigate(`/ops/projects/${project.id}`);
  };

  const doArchiveToggle = async (e, p) => {
    e.stopPropagation();
    try {
      if (p.is_archived) {
        await projectsApi.restore(p.id);
        toast.success('Project restored');
      } else {
        await projectsApi.archive(p.id);
        toast.success('Project archived');
      }
      load();
    } catch {
      toast.error('Action failed');
    }
  };

  // Default "Active Projects" view keeps completed projects in their own category.
  const visible = status === '' ? projects.filter((p) => p.status !== 'completed') : projects;

  return (
    <div data-testid="ops-projects">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-white">Projects</h2>
          <p className="text-gray-400 mt-1">Manage every BuildBroz project from one place.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => setTrashOpen(true)}
            className="border-gray-700 text-gray-300 w-full md:w-auto"
            data-testid="recently-deleted-btn"
          >
            <Trash2 size={15} /> Recently Deleted
          </Button>
          <Button
            onClick={() => setDialogOpen(true)}
            className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold w-full md:w-auto"
            data-testid="projects-new-btn"
          >
            <Plus size={18} /> New Project
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-[#111111] border-gray-800 p-4 mb-6">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search project, customer, or address..."
              className="bg-[#1A1A1A] border-gray-700 text-white focus:border-[#D4AF37] pl-9"
              data-testid="projects-search"
            />
          </div>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className={selectCls}
            data-testid="projects-filter-status"
          >
            <option value="">Active Projects</option>
            {STATUS_OPTIONS.filter((s) => s.value !== 'completed').map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
            <option value="completed">Completed Projects</option>
          </select>
          <select
            value={projectType}
            onChange={(e) => setProjectType(e.target.value)}
            className={selectCls}
            data-testid="projects-filter-type"
          >
            <option value="">All Types</option>
            {TYPE_OPTIONS.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className={selectCls}
            data-testid="projects-sort"
          >
            {SORT_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-2 text-sm text-gray-300 px-2 whitespace-nowrap cursor-pointer">
            <input
              type="checkbox"
              checked={archived}
              onChange={(e) => setArchived(e.target.checked)}
              className="accent-[#D4AF37] h-4 w-4"
              data-testid="projects-filter-archived"
            />
            Show archived
          </label>
        </div>
      </Card>

      {/* Body */}
      {loading ? (
        <div className="text-center py-20 text-gray-500" data-testid="projects-loading">
          <Loader2 size={28} className="mx-auto mb-3 animate-spin text-[#D4AF37]" />
          Loading projects…
        </div>
      ) : error ? (
        <Card className="bg-[#111111] border-gray-800 p-10 text-center" data-testid="projects-error">
          <p className="text-red-400 mb-4">Could not load projects.</p>
          <Button onClick={load} variant="outline" className="border-gray-700 text-gray-300">
            Retry
          </Button>
        </Card>
      ) : visible.length === 0 ? (
        <Card className="bg-[#111111] border-gray-800 p-12 text-center" data-testid="projects-empty">
          <div className="bg-[#1F1F1F] rounded-2xl p-5 mb-5 inline-block">
            <HardHat size={40} className="text-[#D4AF37]" />
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">
            {archived || search || status || projectType
              ? 'No matching projects'
              : 'No projects yet.'}
          </h3>
          <p className="text-gray-500 max-w-md mx-auto mb-6">
            {archived || search || status || projectType
              ? 'Try adjusting your search or filters.'
              : 'Create your first BuildBroz project to start organizing budgets, costs, and project information.'}
          </p>
          {!(archived || search || status || projectType) && (
            <Button
              onClick={() => setDialogOpen(true)}
              className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold"
              data-testid="projects-empty-create"
            >
              <Plus size={18} /> Create Project
            </Button>
          )}
        </Card>
      ) : (
        <>
          {/* Desktop table */}
          <Card className="bg-[#111111] border-gray-800 overflow-hidden hidden md:block">
            <table className="w-full text-sm" data-testid="projects-table">
              <thead>
                <tr className="text-left text-[#D4AF37] text-[10px] uppercase tracking-wider font-brand border-b border-[#D4AF37]/30">
                  <th className="px-4 py-3 font-medium">Project</th>
                  <th className="px-4 py-3 font-medium">Customer</th>
                  <th className="px-4 py-3 font-medium">Location</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Contract Value</th>
                  <th className="px-4 py-3 font-medium">Target</th>
                  <th className="px-4 py-3 font-medium">Updated</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {visible.map((p) => (
                  <tr
                    key={p.id}
                    onClick={() => navigate(`/ops/projects/${p.id}`)}
                    className={`border-b border-gray-800/60 hover:bg-[#1A1A1A] cursor-pointer transition-colors ${
                      p.is_archived ? 'opacity-60' : ''
                    }`}
                    data-testid={`project-row-${p.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-white flex items-center gap-2">
                        {p.name}
                        {p.is_financially_closed && (
                          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full border bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/40" data-testid={`project-closed-badge-${p.id}`}>Financially Closed</span>
                        )}
                        {finMap[p.id] && (
                          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full border whitespace-nowrap ${BUDGET_HEALTH_STYLES[finMap[p.id].budget_health]}`} data-testid={`project-health-${p.id}`}>
                            {budgetHealthLabel(finMap[p.id].budget_health)}
                          </span>
                        )}
                        {p.is_archived && (
                          <span className="text-[10px] uppercase text-gray-400 border border-gray-600 rounded px-1.5 py-0.5">
                            Archived
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {typeLabel(p.project_type, p.custom_project_type)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-300">{p.customer_name || '—'}</td>
                    <td className="px-4 py-3 text-gray-300">{formatLocation(p) || '—'}</td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <ProjectStatusBadge project={p} disabled={p.is_archived} onChanged={load} />
                    </td>
                    <td className="px-4 py-3 text-gray-300">
                      {centsToDollarString(p.current_contract_value_cents) || '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={isOverdue(p) ? 'text-red-400' : 'text-gray-300'}>
                        {fmtDate(p.target_completion_date) || '—'}
                        {isOverdue(p) && <span className="ml-1 text-[10px]">Overdue</span>}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500">{fmtDate(p.updated_at)}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={(e) => doArchiveToggle(e, p)}
                        className="text-gray-500 hover:text-[#D4AF37]"
                        title={p.is_archived ? 'Restore' : 'Archive'}
                        data-testid={`project-archive-toggle-${p.id}`}
                      >
                        {p.is_archived ? <RotateCcw size={16} /> : <Archive size={16} />}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          {/* Mobile cards */}
          <div className="grid grid-cols-1 gap-3 md:hidden">
            {visible.map((p) => (
              <Card
                key={p.id}
                onClick={() => navigate(`/ops/projects/${p.id}`)}
                className={`bg-[#111111] border-gray-800 p-4 ${p.is_archived ? 'opacity-60' : ''}`}
                data-testid={`project-card-${p.id}`}
              >
                <div className="flex justify-between items-start gap-2 mb-2">
                  <div>
                    <div className="font-semibold text-white">{p.name}</div>
                    <div className="text-xs text-gray-500 flex items-center gap-2">
                      {typeLabel(p.project_type, p.custom_project_type)}
                      {finMap[p.id] && (
                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full border whitespace-nowrap ${BUDGET_HEALTH_STYLES[finMap[p.id].budget_health]}`}>
                          {budgetHealthLabel(finMap[p.id].budget_health)}
                        </span>
                      )}
                    </div>
                  </div>
                  <span onClick={(e) => e.stopPropagation()}>
                    <ProjectStatusBadge project={p} disabled={p.is_archived} onChanged={load} />
                  </span>
                </div>
                <div className="text-sm text-gray-400 space-y-1">
                  {p.customer_name && <div>{p.customer_name}</div>}
                  {formatLocation(p) && <div>{formatLocation(p)}</div>}
                  <div className="flex justify-between pt-1">
                    <span className="text-gray-300">
                      {centsToDollarString(p.current_contract_value_cents) || '—'}
                    </span>
                    <span className={isOverdue(p) ? 'text-red-400' : 'text-gray-500'}>
                      {fmtDate(p.target_completion_date) || 'No target date'}
                      {isOverdue(p) && ' · Overdue'}
                    </span>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}

      <ProjectFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        mode="create"
        onSaved={handleSaved}
      />
      <RecentlyDeletedDialog open={trashOpen} onOpenChange={setTrashOpen} onChanged={load} />
    </div>
  );
};
